import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, Subject } from 'rxjs';

import { WebSocketService } from '@core/websocket/websocket.service';
import { WsProtobufService } from '@core/websocket/ws-protobuf.service';
import { SessionService } from '@core/services/session.service';
import {
  ActiveDriverRest,
  DriverReceiveWorkingShiftEvent,
  DriverRestType,
  DriverStateEvent,
  SessionType,
} from '@core/websocket/websocket.models';
import { EventId, RequestPath, ResponseStatus } from '@core/websocket/websocket.constants';

export type RestStep =
  | 'loading'    // Завантаження типів відпочинку
  | 'select'     // Вибір типу відпочинку
  | 'pending'    // Запит надіслано, чекаємо підтвердження
  | 'rejected'   // Запит скасовано/відхилено сервером
  | 'approved'   // Запит підтверджено, можна почати відпочинок
  | 'active'     // Відпочинок в процесі
  | 'expired';   // Час відпочинку вичерпано

@Injectable({ providedIn: 'root' })
export class RestService implements OnDestroy {
  private readonly ws = inject(WebSocketService);
  private readonly proto = inject(WsProtobufService);
  private readonly sessionService = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── State signals ────────────────────────────────────────────────────────

  private readonly _step = signal<RestStep>('loading');
  readonly step = this._step.asReadonly();

  private readonly _restTypes = signal<DriverRestType[]>([]);
  readonly restTypes = this._restTypes.asReadonly();

  private readonly _selectedType = signal<DriverRestType | null>(null);
  readonly selectedType = this._selectedType.asReadonly();

  /** Адреса місця відпочинку (потрібна коли requireAddress = true) */
  private readonly _addressName = signal('');
  readonly addressName = this._addressName.asReadonly();

  /** Чи показувати поле адреси */
  readonly requiresAddress = computed(() => this._selectedType()?.requireAddress ?? false);

  /** Кнопка "Відправити" активна коли тип вибрано і, якщо потрібно, введена адреса */
  readonly canSend = computed(() => {
    const type = this._selectedType();
    if (!type) return false;
    if (type.requireAddress) return this._addressName().trim().length > 0;
    return true;
  });

  /** Назва типу відпочинку для відображення на екранах статусу */
  private readonly _restTitle = signal('');
  readonly restTitle = this._restTitle.asReadonly();

  /**
   * Стріляє лише при відхиленні через реальний WS-івент (DriverRequestRejected).
   * Не стріляє при ініціалізації з lastDriverState/workingShiftData.
   */
  private readonly _rejectedViaWsEvent$ = new Subject<void>();
  readonly rejectedViaWsEvent$ = this._rejectedViaWsEvent$.asObservable();

  /** ID активного запиту (pending/approved) */
  private readonly _activeRequestId = signal<string | null>(null);

  /** ID активного відпочинку (active/expired) */
  private readonly _activeRestId = signal<string | null>(null);

  /**
   * ID запитів/відпочинків, скасованих у цій сесії.
   * Потрібно щоб ігнорувати їх у init(), коли lastDriverState ще застарілий
   * (сервер не встиг надіслати оновлений DriverStateEvent після скасування).
   */
  private readonly _cancelledIds = new Set<string>();

  /** Секунди, що залишились для таймера */
  private readonly _remainingSeconds = signal(0);
  readonly remainingSeconds = this._remainingSeconds.asReadonly();

  /** Повна тривалість поточного відпочинку (секунди). Визначає формат відображення. */
  private readonly _timerDuration = signal(0);

  readonly formattedTime = computed(() => {
    const s = this._remainingSeconds();
    if (this._timerDuration() >= 3600) {
      const hh = Math.floor(s / 3600).toString().padStart(2, '0');
      const mm = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
      const ss = (s % 60).toString().padStart(2, '0');
      return `${hh}:${mm}:${ss}`;
    }
    const mm = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  });

  private timerInterval: ReturnType<typeof setInterval> | null = null;

  // ─── Init ─────────────────────────────────────────────────────────────────

  constructor() {
    // DriverRequestConfirmed → 'approved'
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestConfirmed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (this._step() !== 'pending') return;
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestConfirmedEvent(e.payload);
        if (event.id === this._activeRequestId()) {
          this._step.set('approved');
        }
      });

    // DriverRequestRejected → 'rejected'
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestRejected),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (this._step() !== 'pending') return;
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestEvent(e.payload);
        if (event.id === this._activeRequestId()) {
          this._step.set('rejected');
          this._rejectedViaWsEvent$.next();
        }
      });

    // DriverStateEvent → синхронізація активного відпочинку
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverStateEvent),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        this.handleDriverStateEvent(this.proto.decodeDriverStateEvent(e.payload));
      });

    // DriverRestFinished → повернення до 'select'
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRestFinished),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.stopTimer();
        this._step.set('select');
      });
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /** Ініціалізація сервісу при відкритті сторінки відпочинку */
  async init(): Promise<void> {
    // Якщо вже ініціалізовано — лише підвантажуємо типи якщо потрібно.
    // Таймер продовжує йти, стан не скидається — тому немає флешу при поверненні.
    if (this._step() !== 'loading') {
      try { await this.loadRestTypes(); } catch { /* ignore */ }
      return;
    }

    try {
      await this.loadRestTypes();
    } catch (err) {
      console.error('[Rest] Failed to load rest types:', err);
    }

    // lastDriverState пріоритетний над snapshot: він актуальний і не містить
    // вже скасованих/завершених запитів (на відміну від workingShiftData).
    const liveState = this.sessionService.lastDriverState();
    if (liveState) {
      this.initFromDriverStateEvent(liveState);
      return;
    }

    const wshData = this.sessionService.workingShiftData();
    if (wshData) {
      this.initFromWorkingShiftData(wshData);
    } else {
      this._step.set('select');
    }
  }

  selectType(type: DriverRestType): void {
    this._selectedType.set(type);
    this._addressName.set('');
  }

  setAddress(address: string): void {
    this._addressName.set(address);
  }

  /** Надіслати запит на відпочинок */
  async sendRequest(): Promise<void> {
    const type = this._selectedType();
    const wshData = this.sessionService.workingShiftData();
    if (!type || !wshData) return;

    // Для типів без обов'язкової адреси передаємо displayName як значення
    const addressName = type.requireAddress
      ? this._addressName().trim()
      : type.displayName;

    const sessionId = '';
    const payloadBytes = this.proto.encodeCreateDriverRestPayload(type.id, addressName);
    const body = this.proto.encodeCreateDriverRequest(
      wshData.workingShift.id,
      SessionType.StartDriverRest,
      sessionId,
      payloadBytes,
    );

    const response = await this.ws.sendRequest(RequestPath.CreateDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`CreateDriverRequest failed: ${response.code}`);
    }

    if (response.body?.length) {
      const parsed = this.proto.decodeCreateDriverRequestResponse(response.body);
      this._activeRequestId.set(parsed.id);
    }

    this._restTitle.set(type.displayName);
    this._timerDuration.set(type.duration);
    this._remainingSeconds.set(type.duration);
    this._step.set('pending');
  }

  /** Почати відпочинок після підтвердження запиту */
  async startRest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    const body = this.proto.encodeStartDriverRest(requestId);
    const response = await this.ws.sendRequest(RequestPath.StartDriverRest, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`StartDriverRest failed: ${response.code}`);
    }

    if (response.body?.length) {
      const parsed = this.proto.decodeStartDriverRestResponse(response.body);
      this._activeRestId.set(parsed.id);
    }

    // Таймер буде запущений через DriverStateEvent з ActiveDriverRest.duration
    // Поки ставимо 'active' і чекаємо на DriverStateEvent для синхронізації таймера
    this._step.set('active');
    // Запасний старт — якщо DriverStateEvent не прийде, використаємо поточне значення
    this.startTimer(this._remainingSeconds());
  }

  /** Завершити відпочинок */
  async finishRest(): Promise<void> {
    const restId = this._activeRestId();
    if (!restId) return;

    const body = this.proto.encodeFinishDriverRest(restId);
    await this.ws.sendRequest(RequestPath.FinishDriverRest, body);
    this.stopTimer();
    this.resetState();
  }

  /** Скасувати запит (pending або approved стан) */
  async cancelRequest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    this._cancelledIds.add(requestId);
    const body = this.proto.encodeCancelDriverRequest(requestId);
    const response = await this.ws.sendRequest(RequestPath.CancelDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      this._cancelledIds.delete(requestId);
      throw new Error(`CancelDriverRequest failed: ${response.code}`);
    }
    this.resetState();
  }

  /** Скасувати активний відпочинок */
  async cancelRest(): Promise<void> {
    const restId = this._activeRestId();
    const requestId = this._activeRequestId();
    const idToCancel = restId ?? requestId;
    if (!idToCancel) return;

    this._cancelledIds.add(idToCancel);
    const body = this.proto.encodeCancelDriverRequest(idToCancel);
    const response = await this.ws.sendRequest(RequestPath.CancelDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      this._cancelledIds.delete(idToCancel);
      throw new Error(`CancelDriverRequest failed: ${response.code}`);
    }
    this.stopTimer();
    this.resetState();
  }

  resetState(): void {
    this.stopTimer();
    this._step.set('select');
    this._selectedType.set(null);
    this._addressName.set('');
    this._activeRequestId.set(null);
    this._activeRestId.set(null);
    this._restTitle.set('');
    this._timerDuration.set(0);
    this._remainingSeconds.set(0);
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private async loadRestTypes(): Promise<void> {
    if (this._restTypes().length > 0) return;

    const response = await this.ws.sendRequest(RequestPath.GetRestTypes);
    if (response.status === ResponseStatus.Success && response.body?.length) {
      this._restTypes.set(this.proto.decodeDriverRestTypes(response.body));
    }
  }

  /**
   * Ініціалізація стану з актуального DriverStateEvent.
   * На відміну від snapshot, тут вже немає скасованих/завершених запитів.
   */
  private initFromDriverStateEvent(event: DriverStateEvent): void {
    if (event.driverRest && !this._cancelledIds.has(event.driverRest.id)) {
      this.applyActiveRest(event.driverRest);
      return;
    }

    const restRequest = event.requests.find(
      (r) => r.type === SessionType.StartDriverRest && !this._cancelledIds.has(r.id),
    );
    if (restRequest) {
      this._activeRequestId.set(restRequest.id);
      if (restRequest.status === 'confirmed') {
        this._step.set('approved');
      } else {
        this._step.set('pending');
      }
      return;
    }

    this._step.set('select');
  }

  private initFromWorkingShiftData(data: DriverReceiveWorkingShiftEvent): void {
    // Активний відпочинок має пріоритет
    if (data.driverRest && !this._cancelledIds.has(data.driverRest.id)) {
      this.applyActiveRest(data.driverRest);
      return;
    }

    // Шукаємо активний запит типу StartDriverRest (не скасований)
    const restRequest = data.activeDriverRequests.find(
      (r) => r.type === SessionType.StartDriverRest && !this._cancelledIds.has(r.id),
    );

    if (restRequest) {
      this._activeRequestId.set(restRequest.id);

      // Спроба отримати назву типу з payload
      if (restRequest.payload?.length) {
        try {
          const payload = this.proto.decodeCreateDriverRestPayload(restRequest.payload as Uint8Array);
          const type = this._restTypes().find((t) => t.id === payload.typeId);
          if (type) {
            this._restTitle.set(type.displayName);
            this._timerDuration.set(type.duration);
            this._remainingSeconds.set(type.duration);
          }
        } catch {
          // payload may be in expanded (non-bytes) form — ignore
        }
      }

      if (restRequest.status === 'confirmed') {
        this._step.set('approved');
      } else {
        this._step.set('pending');
      }
      return;
    }

    this._step.set('select');
  }

  private applyActiveRest(rest: ActiveDriverRest): void {
    this._activeRestId.set(rest.id);
    this._restTitle.set(rest.displayName);
    this._timerDuration.set(rest.duration);

    if (rest.startedAt) {
      const elapsed = Math.floor(Date.now() / 1000) - Number(rest.startedAt.seconds);
      const remaining = Math.max(0, rest.duration - elapsed);
      this._remainingSeconds.set(remaining);

      if (remaining > 0) {
        this.startTimer(remaining);
        this._step.set('active');
      } else {
        this._step.set('expired');
      }
    } else {
      // Підтверджено, але ще не почато
      this._remainingSeconds.set(rest.duration);
      this._step.set('approved');
    }
  }

  private handleDriverStateEvent(event: DriverStateEvent): void {
    // Ініціалізація після page refresh — DriverStateEvent прийшов до/замість init()
    if (this._step() === 'loading') {
      this.initFromDriverStateEvent(event);
      return;
    }

    if (event.driverRest) {
      const rest = event.driverRest;
      this._activeRestId.set(rest.id);

      if (rest.startedAt) {
        const elapsed = Math.floor(Date.now() / 1000) - Number(rest.startedAt.seconds);
        const remaining = Math.max(0, rest.duration - elapsed);

        if (this._step() === 'approved') {
          // Щойно почато відпочинок — переходимо в active і синхронізуємо таймер
          this.stopTimer();
          this._remainingSeconds.set(remaining);
          this.startTimer(remaining);
          this._step.set('active');
        } else if (this._step() === 'active') {
          // Ресинхронізація таймера з сервером
          this.stopTimer();
          this._remainingSeconds.set(remaining);
          this.startTimer(remaining);
        }
      } else if (this._step() === 'approved') {
        // Оновлюємо тривалість на frozen timer
        this._timerDuration.set(rest.duration);
        this._remainingSeconds.set(rest.duration);
        this._restTitle.set(rest.displayName);
      }
    }

    // Оновлюємо requestId якщо потрібно
    if (this._step() === 'pending' || this._step() === 'approved') {
      const restRequest = event.requests.find((r) => r.type === SessionType.StartDriverRest);
      if (restRequest) {
        this._activeRequestId.set(restRequest.id);
      }
    }
  }

  private startTimer(initialSeconds: number): void {
    this.stopTimer();
    this._remainingSeconds.set(initialSeconds);

    this.timerInterval = setInterval(() => {
      const current = this._remainingSeconds();
      if (current <= 0) {
        this.stopTimer();
        this._step.set('expired');
        return;
      }
      this._remainingSeconds.update((s) => s - 1);
    }, 1_000);
  }

  private stopTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
}
